import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Pill, type LinkComponent } from './basics';

export interface NavItem {
  to: string;
  label: string;
  badge?: { text: string; tone?: 'ok' | 'warn' };
  /** Show as current page. */
  current?: boolean;
  /** Shown beside the label, and alone when the menu is collapsed. */
  icon?: ReactNode;
}
export interface NavGroup {
  /** Omit for the ungrouped first entries (Overview). */
  label?: string;
  items: NavItem[];
}

export interface AppShellProps {
  orgName: string;
  orgTag: string;
  userInitials: string;
  userName: string;
  nav: NavGroup[];
  linkAs?: LinkComponent;
  /** Status chip in the header, such as "CertTrack connected". */
  connection?: string;
  navFooter?: ReactNode;
  onSearch?: (q: string) => void;
  /** Contents of the organisation menu (switch books, set up new ones). Without it the name is plain text. */
  orgMenu?: (close: () => void) => ReactNode;
  /** Contents of the menu under the avatar (who is signed in, sign out). */
  userMenu?: (close: () => void) => ReactNode;
  children: ReactNode;
}

/** A button that opens a small menu below it; closes on Escape or a click elsewhere. */
function MenuButton({ label, className, button, children, align = 'start' }: {
  label: string;
  className: string;
  button: ReactNode;
  children: (close: () => void) => ReactNode;
  align?: 'start' | 'end';
}) {
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const away = (e: MouseEvent) => { if (!box.current?.contains(e.target as Node)) setOpen(false); };
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', away);
    document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', away); document.removeEventListener('keydown', esc); };
  }, [open]);
  return (
    <div ref={box} className="el-menu-anchor">
      <button type="button" className={className} aria-haspopup="menu" aria-expanded={open} aria-label={label} onClick={() => setOpen((o) => !o)}>
        {button}
      </button>
      {open && <div className="el-popover" data-align={align}>{children(() => setOpen(false))}</div>}
    </div>
  );
}

function NavLinkItem({ item, linkAs: As, grouped }: { item: NavItem; linkAs?: LinkComponent; grouped?: boolean }) {
  const content = (
    <>
      {item.icon && <span className="el-ni-icon" aria-hidden="true">{item.icon}</span>}
      <span className="el-ni-label">{item.label}</span>
      {item.badge && <span className="el-cnt" data-tone={item.badge.tone ?? 'ok'}>{item.badge.text}</span>}
    </>
  );
  const current = item.current ? ('page' as const) : undefined;
  // The name (and any count) shown beside the icon when the menu is collapsed.
  const tip = item.badge ? `${item.label} · ${item.badge.text}` : item.label;
  return As ? (
    <As to={item.to} className="el-ni" aria-current={current} data-tip={tip} data-grouped={grouped || undefined}>{content}</As>
  ) : (
    <a href={item.to} className="el-ni" aria-current={current} data-tip={tip} data-grouped={grouped || undefined}>{content}</a>
  );
}

const COLLAPSED_KEY = 'elysian.nav.collapsed';
function readCollapsed(): boolean {
  try { return localStorage.getItem(COLLAPSED_KEY) === '1'; } catch { return false; }
}
function saveCollapsed(v: boolean) {
  try { localStorage.setItem(COLLAPSED_KEY, v ? '1' : '0'); } catch { /* private window: just not remembered */ }
}

export function AppShell(props: AppShellProps) {
  const { orgName, orgTag, userInitials, userName, nav, linkAs, connection, navFooter, onSearch, orgMenu, userMenu, children } = props;
  const Brand = linkAs;
  const [menuOpen, setMenuOpen] = useState(false);
  // On wide screens the menu can shrink to icons; remembered in this browser.
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const toggleCollapsed = () => setCollapsed((c) => { saveCollapsed(!c); return !c; });
  // Menu sections (#17): all closed when the app opens, apart from the one holding the page you're on;
  // open as many as you like. The icon-only menu always shows every icon.
  const currentSection = nav.find((g) => g.label && g.items.some((i) => i.current))?.label;
  const [openSections, setOpenSections] = useState<ReadonlySet<string>>(() => new Set(currentSection ? [currentSection] : []));
  useEffect(() => {
    if (currentSection) setOpenSections((o) => (o.has(currentSection) ? o : new Set(o).add(currentSection)));
  }, [currentSection]);
  const toggleSection = (label: string) => setOpenSections((o) => {
    const n = new Set(o);
    if (n.has(label)) n.delete(label); else n.add(label);
    return n;
  });
  const brandInner = (
    <>
      <span className="el-brand-mark">
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
          <path d="M3 3h10M3 8h10M3 13h6" stroke="#9FE1CB" strokeWidth="1.6" strokeLinecap="round" />
        </svg>
      </span>
      <span className="el-brand-name">Elysian Ledger</span>
    </>
  );
  return (
    <div className="el-app">
      <a href="#main" className="el-vh">Skip to content</a>
      <header className="el-header">
        {Brand ? <Brand to="/" className="el-brand">{brandInner}</Brand> : <a href="/" className="el-brand">{brandInner}</a>}
        <button type="button" className="el-btn el-menu-btn" aria-expanded={menuOpen} aria-controls="el-nav"
          onClick={() => setMenuOpen((o) => !o)}>Menu</button>
        <span className="el-divider" aria-hidden="true" />
        {orgMenu ? (
          <MenuButton label={`Organisation: ${orgName}. Switch organisation`} className="el-org" button={<>
            <span style={{ fontWeight: 600 }}>{orgName}</span>
            <Pill>{orgTag}</Pill>
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
              <path d="M3 4.5L6 7.5L9 4.5" stroke="#4A6B62" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </>}>{orgMenu}</MenuButton>
        ) : (
          <span className="el-org"><span style={{ fontWeight: 600 }}>{orgName}</span><Pill>{orgTag}</Pill></span>
        )}
        <div className="el-header-end">
          <form
            role="search"
            className="el-search"
            onSubmit={(e) => {
              e.preventDefault();
              const q = new FormData(e.currentTarget).get('q');
              onSearch?.(typeof q === 'string' ? q : '');
            }}
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
              <circle cx="6" cy="6" r="4.5" stroke="#4A6B62" strokeWidth="1.4" />
              <path d="M9.5 9.5L13 13" stroke="#4A6B62" strokeWidth="1.4" strokeLinecap="round" />
            </svg>
            <label htmlFor="el-search-q" className="el-vh">Search</label>
            <input id="el-search-q" name="q" type="search" placeholder="Search invoices, contacts, amounts…" />
          </form>
          {connection && (
            <span className="el-status"><span className="el-status-dot" aria-hidden="true" />{connection}</span>
          )}
          {userMenu ? (
            <MenuButton label={`Signed in as ${userName}. Account menu`} className="el-avatar el-avatar-btn" button={userInitials} align="end">{userMenu}</MenuButton>
          ) : (
            <span className="el-avatar" role="img" aria-label={`Signed in as ${userName}`}>{userInitials}</span>
          )}
        </div>
      </header>
      <div className="el-body">
        <nav id="el-nav" aria-label="Main" className="el-nav" data-open={menuOpen} data-collapsed={collapsed}
          onClick={(e) => { if ((e.target as HTMLElement).closest('a')) setMenuOpen(false); }}>
          <button type="button" className="el-nav-toggle" aria-expanded={!collapsed} aria-controls="el-nav"
            data-tip={collapsed ? 'Expand menu' : undefined} onClick={toggleCollapsed}>
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
              <rect x="1.75" y="2.75" width="14.5" height="12.5" rx="2.25" stroke="currentColor" strokeWidth="1.5" />
              <path d="M6.5 3v12" stroke="currentColor" strokeWidth="1.5" />
              <path d={collapsed ? 'M10 7l2 2-2 2' : 'M12 7l-2 2 2 2'} stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <span className="el-ni-label">{collapsed ? 'Expand menu' : 'Collapse menu'}</span>
          </button>
          {nav.map((g, i) => {
            const open = !g.label || collapsed || openSections.has(g.label);
            const waiting = g.items.filter((it) => it.badge);
            return (
              <div key={g.label ?? `g${i}`} style={{ display: 'contents' }}>
                {g.label && (collapsed ? <div className="el-ng">{g.label}</div> : (
                  <button type="button" className="el-ng el-ng-btn" aria-expanded={open} aria-controls={`el-ns-${i}`} onClick={() => toggleSection(g.label!)}>
                    <span>{g.label}</span>
                    {!open && waiting.length > 0 && (
                      <span className="el-ng-dot" data-tone={waiting.some((it) => it.badge?.tone === 'warn') ? 'warn' : 'ok'}>
                        <span className="el-vh">: {waiting.map((it) => `${it.label} ${it.badge!.text}`).join(', ')}</span>
                      </span>
                    )}
                    <svg className="el-ng-chev" width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
                      <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </button>
                ))}
                {open && (
                  <div id={`el-ns-${i}`} style={{ display: 'contents' }}>
                    {g.items.map((it) => <NavLinkItem key={it.to} item={it} linkAs={linkAs} grouped={Boolean(g.label)} />)}
                  </div>
                )}
              </div>
            );
          })}
          {navFooter}
        </nav>
        <main id="main" className="el-main">{children}</main>
      </div>
    </div>
  );
}
